import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Upload, BarChart3, ChevronRight, Image, FileAudio, Video, Sparkles, ShieldCheck, Activity, AlertTriangle, TrendingUp, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { StatCard } from '../components/Dashboard/StatCard';
import { ScanCard } from '../components/Dashboard/ScanCard';
import { ProgressOverviewSection } from '../components/Dashboard/ProgressOverviewSection';
import { CommunicationStyleWidget } from '../components/Dashboard/CommunicationStyleWidget';
import { WeeklyInsightCard } from '../components/Dashboard/WeeklyInsightCard';
import { ReferralBanner } from '../components/Referral/ReferralBanner';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { consumeNoCreditsPrompt, consumeWelcomeModalPending } from '../utils/upgradePrompts';
import { useScans } from '../hooks/useScans';
import { useWeeklyInsights } from '../hooks/useWeeklyInsights';
import { useScanCredits } from '../hooks/useScanCredits';
import { PLAN_PRICES_USD, formatUsd } from '../data/pricingPlans';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { getMessageCount } from '../utils/scanReport';
export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const [dashboardText, setDashboardText] = useState('');
  const {
    insights
  } = useWeeklyInsights(user?.id);
  const {
    openUpgradeModal
  } = useUpgradeModal();
  // Single source of truth for plan + credits: fetches the `profiles` row on
  // mount, subscribes to realtime updates, re-reads on tab focus, and polls
  // briefly after a payment so the plan flips without a hard reload.
  const {
    isPro,
    scansCount,
    loading: loadingProStatus,
    hasLoaded: creditsLoaded,
    refreshing: refreshingCredits,
    refresh: refreshCredits,
    refetchError: creditsError,
    refreshError
  } = useScanCredits();
  const firstName = user?.user_metadata?.firstName || 'User';
  // Determine user tier: 'pro' | 'active' (has credits) | 'free' (no credits)
  const userTier = isPro ? 'pro' : scansCount > 0 ? 'active' : 'free';
  // Calculate stats from real scans - MOVED BEFORE useEffects that reference them
  const totalScans = scans.length;
  const completedScans = scans.filter(s => s.status === 'completed');
  const toxicScans = completedScans.filter(s => s.risk_level === 'high' || s.risk_level === 'medium');
  const avgScore = completedScans.length > 0 ? Math.round(completedScans.reduce((sum, s) => sum + (s.score || 0), 0) / completedScans.length) : null;
  // Get recent scans (top 3)
  const recentScans = scans.slice(0, 3);
  // Determine pricing display
  const pricingDisplay = isPro ? 'Unlimited Active' : scansCount > 0 ? 'Scans available' : `Single scan — ${formatUsd(PLAN_PRICES_USD.single_scan)}`;
  // Prompts, in priority order: greet a brand-new account once, otherwise
  // surface the out-of-credits options at most once per session. Only runs
  // once credits are genuinely known, so a slow fetch can't mislabel a
  // paying user as out of credits.
  useEffect(() => {
    if (loadingProStatus || !creditsLoaded || !user?.id) return;
    if (consumeWelcomeModalPending()) {
      openUpgradeModal('welcome');
      return;
    }
    if (!isPro && scansCount === 0 && consumeNoCreditsPrompt()) {
      openUpgradeModal('no_credits');
    }
  }, [loadingProStatus, creditsLoaded, user?.id, isPro, scansCount, openUpgradeModal]);
  // Track dashboard view on mount
  useEffect(() => {
    if (!loading && !loadingProStatus && user?.id) {
      trackCriticalEvent('Dashboard_Viewed', {
        total_scans: totalScans,
        completed_scans: completedScans.length,
        is_pro: isPro,
        scans_remaining: scansCount,
        avg_toxicity_score: avgScore
      });
    }
  }, [loading, loadingProStatus, user?.id, totalScans, completedScans.length, isPro, scansCount, avgScore]);
  const fileTypes = [{
    icon: FileText,
    label: 'Text/PDF',
    color: 'text-gray-400'
  }, {
    icon: Image,
    label: 'Screenshots',
    color: 'text-gray-400'
  }, {
    icon: Video,
    label: 'Videos',
    color: 'text-gray-400'
  }, {
    icon: FileAudio,
    label: 'Audio',
    color: 'text-gray-400'
  }];
  return <div className="bg-[#050505] min-h-screen text-white" data-id="element-3695" data-mp="1uvdb037v1n17">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-3696" data-mp="3rndixep8t7gy" />

      <WebsiteHeader data-id="element-3697" data-mp="290m0vs21dd2c" />

      <div className="max-w-6xl mx-auto w-full relative z-10" data-id="element-3698" data-mp="2903tq0du5efn">
        <main className="px-4 md:px-6 pt-8 md:pt-12 pb-24 md:pb-16" data-id="element-3699" data-mp="24ys2jqfbcl5c">
          {/* Welcome Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="mb-10" data-id="element-3700" data-mp="1zznx9avif7ka">
            <div className="flex items-center gap-2 mb-4" data-id="element-3701" data-mp="2np01rxfw6rg9">
              <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-3702" data-mp="2nnf787tkv8ie" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-3703" data-mp="2ipdpbq7w1f8i">
                Operative Terminal Active
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3 uppercase tracking-tight" data-id="element-3704" data-mp="17aqlzrppub11">
              Operative: {firstName}
            </h2>
            <p className="text-gray-400 font-mono text-sm md:text-base max-w-2xl" data-id="element-3705" data-mp="18qcmplndk6kk">
              Forensic surveillance dashboard — monitor threat patterns and
              behavioral anomalies across intercepted communications.
            </p>
          </motion.div>

          {/* Upload Card - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative bg-[#0a0a0a] border-2 border-gray-800 p-8 md:p-10 mb-10 group-hover:border-[#FF0000]/50 transition-all duration-300 group" data-id="element-3706" data-mp="q1xvax95aqj8">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FF0000]" data-id="element-3707" data-mp="c1wu1goyxh38" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FF0000]" data-id="element-3708" data-mp="3olitn5g61ulj" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FF0000]" data-id="element-3709" data-mp="3jmfntisd3kci" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FF0000]" data-id="element-3710" data-mp="2e108l3rhqmg" />

            {/* Scanning Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[scan_2s_ease-in-out_infinite]" data-id="element-3711" data-mp="3tjso1du2gr63" />

            <div className="relative z-20" data-id="element-3712" data-mp="6fcq8rqjsdv6">
              <div className="flex items-center gap-3 mb-6" data-id="element-3713" data-mp="l4ioodhka020">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-[#FF0000]/10 border border-[#FF0000]" data-id="element-3714" data-mp="l40j2q90e9hf">
                  <FileText className="w-5 h-5 text-[#FF0000]" data-id="element-3715" data-mp="u5gki8huj19i" />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-3716" data-mp="u344w1zk92zm">
                  Initialize Forensic Scan
                </h3>
              </div>

              <textarea value={dashboardText} onChange={e => setDashboardText(e.target.value)} className="w-full h-32 bg-[#050505] border border-gray-800 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white p-4 font-mono text-sm resize-none transition-colors" placeholder="Paste your chat conversation here (e.g., texts, emails, or messages) to begin your forensic analysis..." data-id="element-3717" data-mp="1wil162tlflro" />

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 gap-4" data-id="element-3718" data-mp="1is7rnw11zz08">
                <p className="text-[11px] text-gray-500 font-mono max-w-md" data-id="element-3719" data-mp="1mrgo58nhaazh">
                  Fast & Simple: Just copy the messages from your chat app and
                  paste them right here.
                </p>
                <button disabled={!dashboardText.trim()} onClick={() => navigate('/upload', {
                state: {
                  initialText: dashboardText
                }
              })} className="px-8 py-3 bg-[#FF0000] text-white font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-600 transition-colors whitespace-nowrap shadow-[0_0_15px_rgba(255,0,0,0.2)]" data-id="element-3720" data-mp="uzr8tj9hs2zv">
                  Analyze Text
                </button>
              </div>
            </div>
          </motion.div>

          {/* Stats Grid - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-10" data-id="element-3721" data-mp="3v0ms3ef0hvyx">
            <h3 className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-gray-400" data-id="element-3722" data-mp="3h0lpnuj3mghc">
              Case Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3723" data-mp="2w60cyqevtcyd">
            <StatCard value={totalScans.toString()} label="Cases Analyzed" icon={<FileText size={20} data-id="element-3725" data-mp="35xehxdplr4yw" />} data-id="element-3724" data-mp="2s5q7dev04ok1" />
            <StatCard value={toxicScans.length.toString()} label="Threat Detections" icon={<ShieldCheck size={20} data-id="element-3727" data-mp="2d8h17f11hg89" />} data-id="element-3726" data-mp="30z3996b1n0i9" />
            <StatCard value={avgScore !== null ? avgScore.toString() : '—'} label="Avg. Threat Index" icon={<Activity size={20} data-id="element-3729" data-mp="2n7y5bs0n7m24" />} description="Threat Scale: 0–100" data-id="element-3728" data-mp="2dbbmf8rvn52q" />
            </div>
          </motion.div>

          {/* Recent Case Files — directly under the case overview */}
          <section aria-labelledby="recent-cases-heading" className="mb-10" data-id="element-3730" data-mp="2geg2umanqjl">
            <div className="flex items-center justify-between mb-6" data-id="element-3731" data-mp="3tnqwv8j9m1yz">
              <h3 id="recent-cases-heading" className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2" data-id="element-3732" data-mp="3tpl8txck6jqx">
                <span className="w-1 h-4 bg-[#FF0000]" data-id="element-3733" data-mp="1kg3zxllsobo8" />
                Recent Case Files
              </h3>
              {recentScans.length > 0 && <Link to="/all-scans" className="text-sm font-mono text-[#FF0000] hover:text-[#FF0000]/80 transition-colors uppercase tracking-wide" data-id="element-3734" data-mp="1bdmdp7653j18">
                  View All →
                </Link>}
            </div>

            {loading ? <div className="space-y-4" role="status" aria-label="Loading case files" data-id="element-3735" data-mp="1pcugsb5fbbap">
                {[1, 2].map(i => <div key={i} className="bg-[#0a0a0a] border border-gray-800 p-6 animate-pulse" data-id="element-3736" data-mp="1ub5q8krt3jse">
                    <div className="flex items-center gap-4" data-id="element-3737" data-mp="2mtjeptr95tet">
                      <div className="w-12 h-12 bg-gray-800" data-id="element-3738" data-mp="2mv4b7ox4nmfb" />
                      <div className="flex-1 space-y-2" data-id="element-3739" data-mp="2wnynidckf2hv">
                        <div className="h-4 bg-gray-800 w-1/3" data-id="element-3740" data-mp="2rqghovtuh3e3" />
                        <div className="h-3 bg-gray-900 w-1/4" data-id="element-3741" data-mp="2ro3xsd4j0iye" />
                      </div>
                    </div>
                  </div>)}
              </div> : recentScans.length === 0 ? <div className="bg-[#0a0a0a] border border-gray-800 p-10 text-center" data-id="element-3742" data-mp="2dqfeh9q3xjvg">
                <FileText className="mx-auto w-8 h-8 text-gray-600 mb-4" data-id="element-3743" data-mp="2ds8o8xw0ok7o" />
                <h4 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-3744" data-mp="28ye63yw28dap">
                  No Cases Filed
                </h4>
                <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto font-mono" data-id="element-3745" data-mp="1z1j5nag882ts">
                  Your forensic case files will appear here. Paste a
                  conversation above to begin.
                </p>
                <Link to="/upload" className="inline-flex items-center justify-center px-6 py-3 bg-[#FF0000] text-white font-bold uppercase tracking-wider hover:bg-[#FF0000]/90 transition-colors" data-id="element-3746" data-mp="1q0l20t9p9xbq">
                  Initialize First Scan
                </Link>
              </div> : <div className="space-y-4" data-id="element-3747" data-mp="ehe347z3ysq4">
                {recentScans.map(scan => <ScanCard key={scan.id} scanId={scan.id} title={scan.title} timeAgo={getTimeAgo(scan.created_at)} score={scan.score || 0} riskLevel={getRiskLevelText(scan.risk_level)} messages={getMessageCount(scan)} tags={getTopPatterns(scan.patterns)} status={scan.status} patterns={scan.patterns || []} summary={isPro || scan.is_unlocked ? scan.analysis?.summary : undefined} isUnlocked={isPro || scan.is_unlocked} data-id="element-3748" data-mp="jhsdhwo6merb" />)}
              </div>}
          </section>

          {/* Weekly Insight */}
          {insights?.primaryInsight && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="mb-10" data-id="element-3749" data-mp="1jjfayxs82l29">
              <WeeklyInsightCard insight={insights.primaryInsight} data-id="element-3750" data-mp="22h9fayieo8eb" />
            </motion.div>}

          {/* Progress Overview Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-3751" data-mp="1xjgc8ecefui9">
            <ProgressOverviewSection scans={scans} loading={loading} data-id="element-3752" data-mp="2essbw00n6t1y" />
          </motion.div>

          {/* Communication Style Widget */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.35
        }} data-id="element-3753" data-mp="2jsepwboaodbt">
            <CommunicationStyleWidget scans={scans} data-id="element-3754" data-mp="1w3c7v2pr0igs" />
          </motion.div>

          {/* Subscription Status - Forensic Style with 3-tier display */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className={`relative bg-[#0a0a0a] border-2 p-6 md:p-8 mb-10 ${userTier === 'pro' ? 'border-[#FFD700]/50' : userTier === 'active' ? 'border-green-500/30' : 'border-gray-700'}`} data-id="element-3755" data-mp="1n1mjy6v2s1nr">
            {/* Corner Brackets */}
            <div className={`absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3756" data-mp="t72ggv75n424" />
            <div className={`absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3757" data-mp="ka1wr1v15u50" />
            <div className={`absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3758" data-mp="185ea0sz8dwp2" />
            <div className={`absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-3759" data-mp="20n0hbw5tub4q" />

            <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-3760" data-mp="237y5yjmi2ews">
              <div data-id="element-3761" data-mp="286j9ot0yngpz">
                <div className="flex items-center gap-3 mb-2" data-id="element-3762" data-mp="285q8e7krtjf8">
                  <p className={`text-[10px] font-mono uppercase tracking-widest ${userTier === 'pro' ? 'text-[#FFD700]' : userTier === 'active' ? 'text-green-500' : 'text-gray-500'}`} data-id="element-3763" data-mp="2d4b7u4s77z2m">
                    Clearance Level
                  </p>
                  {/* Tier Badge */}
                  <span className={`px-2 py-0.5 border text-[10px] font-mono font-bold uppercase tracking-widest ${userTier === 'pro' ? 'bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30' : userTier === 'active' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-gray-800 text-gray-500 border-gray-700'}`} data-id="element-3764" data-mp="2r314jjb3vwqg">
                    {loadingProStatus ? 'Checking…' : userTier === 'pro' ? '★ PRO' : userTier === 'active' ? 'PAY-PER-SCAN' : 'FREE'}
                  </span>
                </div>
                <div className="flex items-baseline gap-3" data-id="element-3765" data-mp="8ytvjfo3zgg6">
                  {loadingProStatus ? <p className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-3766" data-mp="40io1bladi14">
                      Verifying...
                    </p> : userTier === 'pro' ? <p className="text-2xl font-black text-[#FFD700] uppercase tracking-tight" data-id="element-3767" data-mp="37gzaakmf2k5k">
                      ∞ Unlimited Scans
                    </p> : <p className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-3768" data-mp="3hdu3cnzx7scg">
                      Scan Credits:{' '}
                      <span className={scansCount > 0 ? 'text-green-500' : 'text-[#FF0000]'} data-id="element-3769" data-mp="2tnhxrjs5e7p1">
                        {scansCount}
                      </span>
                    </p>}
                </div>
                {/* Tier description */}
                <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-3770" data-mp="u9zwnbudfn13">
                  {loadingProStatus ? 'Reading your latest plan from your account…' : userTier === 'pro' ? 'Full forensic access — all features unlocked' : userTier === 'active' ? `${scansCount} scan${scansCount !== 1 ? 's' : ''} remaining — purchase more or upgrade to Pro` : 'No scan credits — purchase a scan or upgrade to Pro'}
                </p>
                {/* Non-blocking status line: the card above keeps showing the
                    last known values while we retry in the background. */}
                {!loadingProStatus && (creditsError || refreshError) && <p className="text-xs font-mono text-[#FFD700] mt-2" data-id="element-3771" data-mp="34ykw9xm58tvm">
                    {creditsError ? 'Couldn’t reach your account — tap refresh to try again.' : 'Showing last known credits — tap refresh to update.'}
                  </p>}
                <button type="button" onClick={() => {
                void refreshCredits();
              }} disabled={refreshingCredits} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-gray-700 text-gray-300 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ease-out hover:border-gray-500 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-3772" data-mp="2zz836v4oiqkl">
                  <RefreshCw size={12} className={refreshingCredits ? 'animate-spin' : undefined} data-id="element-3773" data-mp="22bg39te8lb9b" />
                  {refreshingCredits ? 'Refreshing' : 'Refresh credits'}
                </button>
              </div>
              {!isPro && !loadingProStatus && <button onClick={() => {
              trackCriticalEvent('Upgrade_CTA_Clicked', {
                cta_location: 'subscription_card',
                current_plan: userTier,
                scans_remaining: scansCount
              });
              openUpgradeModal('plans');
            }} className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFD700] text-black font-bold uppercase tracking-wider hover:bg-[#FFD700]/90 transition-all text-sm shadow-[0_0_20px_rgba(255,215,0,0.4)]" data-id="element-3774" data-mp="1jp07577s1nqh">
                  {userTier === 'active' ? 'Upgrade to Pro' : 'Get Started'}
                  <ChevronRight size={16} data-id="element-3775" data-mp="2qb2o2p9dhh4g" />
                </button>}
            </div>
          </motion.div>

          {/* Referral viral loop */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.45
        }} className="mb-10" data-id="element-3776" data-mp="2sfze1b66zauw">
            <ReferralBanner data-id="element-3777" data-mp="3755a4q9qgkyk" />
          </motion.div>

          {/* Quick Actions - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mb-10" data-id="element-3778" data-mp="3c4q8ayh2wpa8">
            <h3 className="text-lg font-black text-white mb-6 uppercase tracking-tight flex items-center gap-2" data-id="element-3779" data-mp="3q37w0w6zi1s5">
              <span className="w-1 h-4 bg-[#FF0000]" data-id="element-3780" data-mp="1qsq91h48j0ez" />
              Command Center
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3781" data-mp="1vrt3k7ml9o8p">
              <Link to="/upload" className="group" data-id="element-3782" data-mp="1o6zbbm14rv1m">
                <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 group-hover:border-[#FF0000]/50 transition-all duration-300 h-full" data-id="element-3783" data-mp="1jfh9ywv73sg4">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3784" data-mp="1eh5w8b1s4xsm" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3785" data-mp="2751ql93vmdm8" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3786" data-mp="227a1emum95lq" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3787" data-mp="1s7svq5fd6rpr" />

                  <div className="flex items-center gap-4" data-id="element-3788" data-mp="lta5ro2t024a">
                    <div className="flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 text-gray-400 group-hover:bg-[#FF0000]/10 group-hover:border-[#FF0000] group-hover:text-[#FF0000] transition-all duration-300" data-id="element-3789" data-mp="gs49xhdujyqj">
                      <Upload size={24} data-id="element-3790" data-mp="2af34tdoir4bo" />
                    </div>
                    <div data-id="element-3791" data-mp="1vpf1y80ovxlu">
                      <h4 className="font-bold text-white mb-1 uppercase tracking-wide text-sm group-hover:text-[#FF0000] transition-colors" data-id="element-3792" data-mp="1vq6ypm60w1dp">
                        Initialize Scan
                      </h4>
                      <p className="text-xs text-gray-500 font-mono" data-id="element-3793" data-mp="2oe2os8136v1i">
                        Submit evidence for forensic analysis
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
              <Link to="/all-scans" className="group" data-id="element-3794" data-mp="2jdos1m4xr7is">
                <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 group-hover:border-[#FF0000]/50 transition-all duration-300 h-full" data-id="element-3795" data-mp="2jgazzmvqnp1j">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3796" data-mp="17zb8gth7te8k" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3797" data-mp="3m8wzt0sg6r44" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3798" data-mp="3v7s6sf3bg58h" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-3799" data-mp="3r3ss821ljghr" />

                  <div className="flex items-center gap-4" data-id="element-3800" data-mp="2yi125n1b91oy">
                    <div className="flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 text-gray-400 group-hover:bg-[#FF0000]/10 group-hover:border-[#FF0000] group-hover:text-[#FF0000] transition-all duration-300" data-id="element-3801" data-mp="2yfew5ru2iu8u">
                      <BarChart3 size={24} data-id="element-3802" data-mp="2jra8sak2lujq" />
                    </div>
                    <div data-id="element-3803" data-mp="2fr020vogf03t">
                      <h4 className="font-bold text-white mb-1 uppercase tracking-wide text-sm group-hover:text-[#FF0000] transition-colors" data-id="element-3804" data-mp="2fpp0z3me86ge">
                        Access Case Files
                      </h4>
                      <p className="text-xs text-gray-500 font-mono" data-id="element-3805" data-mp="nnw1i05mqnhq">
                        Review forensic intelligence reports
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>

          {/* Upgrade CTA - Only for non-Pro users */}
          {!isPro && !loadingProStatus && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="relative bg-gradient-to-r from-[#FF0000] via-[#FF0000]/90 to-[#FF0000] border-2 border-[#FF0000] p-8 md:p-12 text-white overflow-hidden" data-id="element-3806" data-mp="4re3j106trd1">
              {/* Scanning Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-3807" data-mp="3al6qhgjouw34" />

              {/* Grid Overlay */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-3808" data-mp="30u2gywcs83fo" />

              <div className="relative z-10 text-center max-w-2xl mx-auto" data-id="element-3809" data-mp="2qvec6z9t5ktx">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 border border-white/30 mb-4" data-id="element-3810" data-mp="2muue9theud6q">
                  <Sparkles className="w-6 h-6 text-white" data-id="element-3811" data-mp="2cxy29dsaz8xf" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black mb-3 uppercase tracking-tight text-white" data-id="element-3812" data-mp="2d02hgrvnjud0">
                  Unlock Full Forensic Access
                </h3>
                <p className="text-white/90 mb-8 text-base md:text-lg font-mono" data-id="element-3813" data-mp="b7wb9hdhvp67">
                  Gain unlimited forensic scans, deep behavioral pattern
                  analysis, and priority processing with Pro clearance.
                </p>
                <button onClick={() => {
              trackCriticalEvent('Upgrade_CTA_Clicked', {
                cta_location: 'dashboard_banner',
                current_plan: userTier,
                scans_remaining: scansCount
              });
              openUpgradeModal('plans');
            }} className="px-8 py-4 bg-white text-[#FF0000] font-bold uppercase tracking-wider hover:bg-gray-100 transition-all shadow-lg" data-id="element-3814" data-mp="36gtlkzi4qnyu">
                  Upgrade Clearance — 30% Off
                </button>
              </div>
            </motion.div>}
        </main>
      </div>
    </div>;
};
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}
function getRiskLevelText(riskLevel: 'low' | 'medium' | 'high' | null): string {
  if (!riskLevel) return 'Processing';
  return riskLevel === 'high' ? 'High Risk' : riskLevel === 'medium' ? 'Medium Risk' : 'Low Risk';
}
function getTopPatterns(patterns: any[] | null): string[] {
  if (!patterns || !Array.isArray(patterns) || patterns.length === 0) return [];
  return patterns.slice(0, 2).map(p => p.type || p);
}